import { Link } from "react-router-dom";
import { ShieldCheck, KeyRound, BrainCircuit, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SITE, IMAGES } from "@/config/site";

const FEATURES = [
  {
    Icon: ShieldCheck,
    title: "HIPAA-ready workflows",
    text: "Documentation, scheduling, and billing flows designed around HIPAA requirements for regulated US care.",
  },
  {
    Icon: KeyRound,
    title: "Mandatory MFA · BAA-first",
    text: "Multi-factor authentication for every user and a Business Associate Agreement signed before onboarding begins.",
  },
  {
    Icon: BrainCircuit,
    title: "CareIQ AI copilot",
    text: "A multi-model AI copilot embedded in every workflow — from intake and care plans to compliance checks.",
  },
];

const VERTICALS = ["Home health", "Hospice", "Mental health", "Group living", "Assisted living"];

/** Home page spotlight for Proxavi — dark, full-bleed blueprint section. */
export function ProxaviSpotlight() {
  const product = SITE.products.proxavi;

  return (
    <section
      className="relative overflow-hidden bg-steel text-white blueprint-grid py-24"
      data-testid="proxavi-spotlight"
    >
      <div
        className="pointer-events-none absolute -left-32 top-1/2 h-[28rem] w-[28rem] -translate-y-1/2 rounded-full bg-royal/25 blur-3xl"
        aria-hidden="true"
      />
      <div className="relative max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded border border-royal/40 bg-royal/15 px-3 py-1 font-mono-sub text-xs uppercase tracking-wider text-royal-bright">
            <span className="h-1.5 w-1.5 rounded-full bg-royal-bright" aria-hidden="true" />
            Product Spotlight · {product.category}
          </div>
          <h2 className="font-poster text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight mt-6">
            Run your entire care operation —{" "}
            <span className="text-royal-bright">smarter, faster, and fully compliant</span> — from one
            intelligent platform.
          </h2>
          <p className="text-base text-slate-300 mt-6 max-w-2xl leading-relaxed">{product.description}</p>

          <ul className="mt-6 flex flex-wrap gap-2" aria-label="Care verticals">
            {VERTICALS.map((v) => (
              <li
                key={v}
                className="rounded border border-slate-700 bg-steel-light px-3 py-1 font-mono-sub text-[11px] text-slate-200"
              >
                {v}
              </li>
            ))}
          </ul>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {FEATURES.map(({ Icon, title, text }) => (
              <div
                key={title}
                data-testid={`proxavi-feature-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-xl border border-slate-800 bg-steel-light/60 p-5 backdrop-blur transition-colors hover:border-royal/50"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-royal/40 bg-royal/15 text-royal-bright">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="font-poster font-bold text-base mt-4">{title}</h3>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/products#proxavi"
              data-testid="proxavi-waitlist-cta"
              className="inline-flex items-center gap-2 rounded bg-royal px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-royal-dark"
            >
              Join the Proxavi waitlist <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="proxavi-consultation-cta"
              className="rounded border border-slate-600 px-6 py-3.5 text-sm font-medium text-slate-200 transition-colors hover:bg-steel-light"
            >
              Request a Consultation
            </Link>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-5" delay={120}>
          <div className="relative rounded-2xl border border-slate-700 p-2 bg-steel-light/60 shadow-2xl">
            <div className="relative aspect-[4/5] overflow-hidden rounded-xl">
              <img
                src={IMAGES.proxavi}
                alt="Clinician reviewing a patient's care plan on a tablet with Proxavi"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-steel via-steel/30 to-transparent" />
              <div className="absolute inset-x-4 bottom-4 rounded-xl border border-white/15 bg-white/10 p-4 backdrop-blur-md">
                <p className="font-mono-sub text-[10px] uppercase tracking-wider text-royal-bright">
                  CareIQ · multi-model copilot
                </p>
                <p className="font-poster font-bold text-lg mt-1">Compliance built into every click.</p>
                <p className="text-xs text-slate-300 mt-1">
                  {product.status} — onboarding opens to care organizations on request.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
